import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button
} from '@mui/material';
import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { useSchools } from '../contexts/SchoolsContext';
import { useSchoolsUS } from '../contexts/SchoolsContextUS';
import { usePrograms } from '../contexts/ProgramsContext';
import { useEvents } from '../contexts/EventsContext';
import { useUser } from '../contexts/UserContext';

interface ActivityLogEntry {
  timestamp: number;
  uid: string;
  actionTaken: string;
  actionSubject: string;
}

const Reports: React.FC = () => {
  const { user } = useAuth();
  const { schools } = useSchools();
  const { schools: schoolsUS } = useSchoolsUS();
  const { programs } = usePrograms();
  const { events } = useEvents();
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [userData, setUserData] = useState<Record<string, { firstName: string; lastName: string; schoolName: string }>>({});
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchedEvents, setFetchedEvents] = useState<Record<string, { title?: string; name?: string; hostName?: string; host?: string }>>({});
  
  // Filter states
  const [startDate, setStartDate] = useState<string>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
  });
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [eventFilter, setEventFilter] = useState<string[]>([]);
  const [eventDetailFilter, setEventDetailFilter] = useState<string>('');
  const [highSchoolFilter, setHighSchoolFilter] = useState<string>('');

  // Helper function to get user's high school name from user ID
  const getUserHighSchool = (userId: string): string => {
    const userInfo = userData[userId];
    return userInfo?.schoolName || 'Unknown School';
  };

  // Helper function to get user name from user ID
  const getUserName = (userId: string): string => {
    const userInfo = userData[userId];
    if (userInfo && userInfo.firstName && userInfo.lastName) {
      return `${userInfo.lastName}, ${userInfo.firstName}`;
    }
    return userId;
  };

  // Get unique values for filter dropdowns
  // For User filter: only show users with activity in the selected date range, sorted alphabetically
  const uniqueUserIds = activityLogs
    .filter(log => {
      // Filter by date range only
      const logDate = new Date(log.timestamp);
      const logDateString = logDate.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      });
      return logDateString >= startDate && logDateString <= endDate;
    })
    .map(log => log.uid)
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort((a, b) => {
      // Sort alphabetically by user name
      const nameA = getUserName(a).toLowerCase();
      const nameB = getUserName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  const uniqueEvents = activityLogs
    .map(log => log.actionTaken)
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort();
  // For High School filter: only show high schools with activity in the selected date range, sorted alphabetically
  const uniqueHighSchools = activityLogs
    .filter(log => {
      // Filter by date range only
      const logDate = new Date(log.timestamp);
      const logDateString = logDate.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      });
      return logDateString >= startDate && logDateString <= endDate;
    })
    .map(log => getUserHighSchool(log.uid))
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort();
  // Note: uniqueEventDetails is available for future use if needed
  // const uniqueEventDetails = activityLogs
  //   .map(log => log.actionSubject)
  //   .filter((value, index, self) => self.indexOf(value) === index)
  //   .sort();

  // Convert date string (mm/dd/yy) to timestamp
  const dateStringToTimestamp = (dateStr: string): number => {
    const [month, day, year] = dateStr.split('/');
    const fullYear = parseInt(year) < 50 ? 2000 + parseInt(year) : 1900 + parseInt(year);
    return new Date(fullYear, parseInt(month) - 1, parseInt(day)).getTime();
  };

  // Convert timestamp to date string (mm/dd/yy) - available for future use
  // const timestampToDateString = (timestamp: number): string => {
  //   const date = new Date(timestamp);
  //   return date.toLocaleDateString('en-US', { 
  //     month: '2-digit', 
  //     day: '2-digit', 
  //     year: '2-digit' 
  //   });
  // };

  // Convert timestamp to readable date/time
  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Helper function to get school name from school ID
  const getSchoolName = (schoolId: string): string => {
    // First check Canadian schools
    const canadianSchool = schools.find(s => s.id === schoolId);
    if (canadianSchool) {
      return canadianSchool.name;
    }
    
    // Then check US schools
    const usSchool = schoolsUS.find(s => s.id === schoolId);
    if (usSchool) {
      return usSchool.name;
    }
    
    // If not found in either, return the original ID
    return schoolId;
  };

  // Helper function to get program name from program ID
  const getProgramName = (programId: string): string => {
    const program = programs.find(p => p.id === programId);
    return program ? program.program : programId;
  };

  // Helper function to get school name for a program
  const getProgramSchoolName = (programId: string): string => {
    const program = programs.find(p => p.id === programId);
    return program ? program.schoolName : '';
  };

  // Helper function to fetch missing events from Firebase
  const fetchMissingEvents = async (eventIds: string[]): Promise<void> => {
    // Filter out events we already have
    const missingEventIds = eventIds.filter(eventId => {
      // Check if we have it in local events array
      const hasInLocal = events.some(e => e.id === eventId);
      // Check if we've already fetched it
      const hasInFetched = fetchedEvents[eventId];
      return !hasInLocal && !hasInFetched;
    });

    if (missingEventIds.length === 0) return;

    try {
      // Events are stored at schoolEvent/{schoolId}/{userId}/{eventId}
      // We need to search through all schools and users to find the events
      const schoolEventRef = ref(database, 'schoolEvent');
      const snapshot = await get(schoolEventRef);
      
      const newFetchedEvents: Record<string, { title?: string; name?: string; hostName?: string; host?: string }> = {};
      
      if (snapshot.exists()) {
        const schoolsData = snapshot.val();
        for (const schoolId in schoolsData) {
          const schoolEventsByUser = schoolsData[schoolId];
          for (const userId in schoolEventsByUser) {
            const userEvents = schoolEventsByUser[userId];
            for (const eventId of missingEventIds) {
              if (userEvents[eventId] && !newFetchedEvents[eventId]) {
                const eventData = userEvents[eventId];
                const eventContent = eventData?.eventContent || {};
                
                newFetchedEvents[eventId] = {
                  title: eventContent.title,
                  name: eventContent.name,
                  hostName: eventContent.hostName,
                  host: eventContent.host
                };
              }
            }
          }
        }
      }

      if (Object.keys(newFetchedEvents).length > 0) {
        setFetchedEvents(prev => ({ ...prev, ...newFetchedEvents }));
      }
    } catch (err) {
      console.error('Error fetching missing events from Firebase:', err);
    }
  };

  // Helper function to get event name and host from event ID
  const getEventName = (eventId: string): string => {
    // First check local events array
    const event = events.find(e => e.id === eventId);
    if (event) {
      return event.name || event.title || 'Unknown Event';
    }
    
    // Then check fetched events cache
    const fetchedEvent = fetchedEvents[eventId];
    if (fetchedEvent) {
      return fetchedEvent.title || fetchedEvent.name || 'Unknown Event';
    }
    
    return eventId;
  };

  // Helper function to get event host from event ID
  const getEventHost = (eventId: string): string => {
    // First check local events array
    const event = events.find(e => e.id === eventId);
    if (event) {
      return event.host || event.hostName || '';
    }
    
    // Then check fetched events cache
    const fetchedEvent = fetchedEvents[eventId];
    if (fetchedEvent) {
      return fetchedEvent.hostName || fetchedEvent.host || '';
    }
    
    return '';
  };



  // Helper function to format event detail with readable names
  const formatEventDetail = (actionTaken: string, actionSubject: string): string => {
    // Handle school-related events (including athletics referrals)
    if ((actionTaken.includes('School') && !actionTaken.includes('Program')) || 
        actionTaken === 'External Athletics Website Referral') {
      const schoolName = getSchoolName(actionSubject);
      // Debug logging for school name resolution
      if (actionTaken === 'External School Twitter Referral') {
        console.log('External School Twitter Referral debug:', {
          actionSubject,
          schoolName,
          canadianSchoolsCount: schools.length,
          usSchoolsCount: schoolsUS.length,
          foundInCanadian: schools.find(s => s.id === actionSubject),
          foundInUS: schoolsUS.find(s => s.id === actionSubject)
        });
      }
      return schoolName !== actionSubject ? schoolName : actionSubject;
    }
    
    // Handle program-related events
    if (actionTaken.includes('Program')) {
      const programName = getProgramName(actionSubject);
      const schoolName = getProgramSchoolName(actionSubject);
      if (programName !== actionSubject) {
        return schoolName ? `${programName} - ${schoolName}` : programName;
      }
    }
    
    // Handle event-related activities
    if (actionTaken.includes('Event')) {
      const eventName = getEventName(actionSubject);
      const eventHost = getEventHost(actionSubject);
      // Format if event was found (eventName will differ from actionSubject when found)
      // Also format if we have a host name (indicates we found event details)
      if (eventName !== actionSubject) {
        // Event was found - format with host if available
        return eventHost ? `${eventName} - ${eventHost}` : eventName;
      } else if (eventHost) {
        // Event not found in local array yet, but we have host from fetched cache
        // This means async fetch is in progress or completed
        return eventHost ? `${eventName} - ${eventHost}` : eventName;
      } else {
        // Event not found - likely deleted. Show EventID with indicator
        return `${actionSubject} (deleted event)`;
      }
    }
    
    // For other events, return the original actionSubject
    return actionSubject;
  };

  // Fetch activity logs from Firebase
  const fetchActivityLogs = async () => {
    if (!user?.uid) {
      console.log('No user UID available, skipping fetch');
      return;
    }

    console.log('Starting fetchActivityLogs with user UID:', user.uid);
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching activity logs...');
      console.log('Start date:', startDate, 'End date:', endDate);
      
      const activityLogRef = ref(database, 'activity_log');
      
      // First, let's get all logs to see what exists
      console.log('Fetching all activity logs first...');
      const allLogsSnapshot = await get(activityLogRef);
      console.log('All logs snapshot exists:', allLogsSnapshot.exists());
      console.log('All logs count:', allLogsSnapshot.size);
      
      if (allLogsSnapshot.exists()) {
        const allLogs: ActivityLogEntry[] = [];
        allLogsSnapshot.forEach((childSnapshot) => {
          const logData = childSnapshot.val();
          allLogs.push({
            timestamp: logData.timestamp,
            uid: logData.uid,
            actionTaken: logData.actionTaken,
            actionSubject: logData.actionSubject
          });
        });
        console.log('All logs found:', allLogs.length);
        
        // For now, let's use all logs and do client-side filtering
        // This is more reliable than Firebase date queries
        allLogs.sort((a, b) => b.timestamp - a.timestamp);
        setActivityLogs(allLogs);
        
        // Fetch user data for all unique UIDs in the logs
        const uniqueUids = allLogs.map(log => log.uid).filter((value, index, self) => self.indexOf(value) === index);
        await fetchUserData(uniqueUids);
        return;
      }

      // If no logs found at all, set empty array
      setActivityLogs([]);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError('Failed to load activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  // Fetch user data for all UIDs in activity logs
  const fetchUserData = async (uids: string[]) => {
    if (uids.length === 0) return;

    setUserDataLoading(true);
    try {
      console.log('Fetching user data for UIDs:', uids);
      const userDataMap: Record<string, { firstName: string; lastName: string; schoolName: string }> = {};
      
      // Fetch user data for each unique UID
      for (const uid of uids) {
        if (uid === 'demo-user') {
          userDataMap[uid] = { firstName: 'Demo', lastName: 'User', schoolName: 'Demo School' };
          continue;
        }
        
        try {
          const userRef = ref(database, `user/${uid}`);
          const userSnapshot = await get(userRef);
          
          if (userSnapshot.exists()) {
            const userInfo = userSnapshot.val();
            const profileData = userInfo.ProfileData || {};
            
            console.log(`User data for ${uid}:`, userInfo);
            console.log(`ProfileData for ${uid}:`, profileData);
            
            userDataMap[uid] = {
              firstName: profileData['First Name'] || 'Unknown',
              lastName: profileData['Last Name'] || 'User',
              schoolName: profileData['High School'] || 'Unknown School'
            };
          } else {
            userDataMap[uid] = { firstName: 'Unknown', lastName: 'User', schoolName: 'Unknown School' };
          }
        } catch (userErr) {
          console.error(`Error fetching user data for ${uid}:`, userErr);
          userDataMap[uid] = { firstName: 'Unknown', lastName: 'User', schoolName: 'Unknown School' };
        }
      }
      
      setUserData(userDataMap);
      console.log('User data fetched:', userDataMap);
    } catch (err) {
      console.error('Error fetching user data:', err);
      // Don't set error state for user data fetching failures
    } finally {
      setUserDataLoading(false);
    }
  };

  // Filter logs based on current filters
  const filteredLogs = activityLogs.filter(log => {
    // Date filtering - convert to date strings for comparison
    const logDate = new Date(log.timestamp);
    const logDateString = logDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
    
    // Check if log date is within the selected range (inclusive)
    const matchesDate = logDateString >= startDate && logDateString <= endDate;
    
    // Other filters
    const matchesUserId = !userIdFilter || log.uid.includes(userIdFilter);
    const matchesEvent = eventFilter.length === 0 || eventFilter.includes(log.actionTaken);
    const formattedEventDetail = formatEventDetail(log.actionTaken, log.actionSubject);
    const matchesEventDetail = !eventDetailFilter || formattedEventDetail.toLowerCase().includes(eventDetailFilter.toLowerCase());
    const matchesHighSchool = !highSchoolFilter || getUserHighSchool(log.uid).includes(highSchoolFilter);
    
    return matchesDate && matchesUserId && matchesEvent && matchesEventDetail && matchesHighSchool;
  });

  // Debug logging
  console.log('Activity logs count:', activityLogs.length);
  console.log('Filtered logs count:', filteredLogs.length);
  console.log('Loading states - loading:', loading, 'userDataLoading:', userDataLoading);
  console.log('Error state:', error);

  // Fetch logs when component mounts and user is available
  useEffect(() => {
    if (user?.uid) {
      console.log('User available, fetching activity logs...');
      fetchActivityLogs();
    } else {
      console.log('User not available yet, waiting...');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]); // Fetch when user becomes available

  // Fetch missing events for Event Pageview entries
  useEffect(() => {
    if (activityLogs.length > 0) {
      // Extract unique event IDs from Event Pageview entries
      const eventPageviewLogs = activityLogs.filter(log => log.actionTaken === 'Event Pageview');
      const eventIds = eventPageviewLogs
        .map(log => log.actionSubject)
        .filter((value, index, self) => self.indexOf(value) === index);
      
      if (eventIds.length > 0) {
        fetchMissingEvents(eventIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityLogs]); // Fetch missing events when activity logs change

  return (
    <Box sx={{ width: '100%', mt: 4, px: { xs: 0, sm: 1, md: 2 } }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Activity Log Reports
        </Typography>
        
        {/* Filters */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={3}>
            {/* Date Filters - First Row */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Start Date"
                type="text"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="mm/dd/yy"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="End Date"
                type="text"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="mm/dd/yy"
                fullWidth
                size="small"
              />
            </Grid>
            
            {/* Spacer to push other filters to the next row on larger screens */}
            <Grid item xs={12} md={6}></Grid>
            
            {/* Other Filters - Second Row */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>User</InputLabel>
                <Select
                  value={userIdFilter}
                  label="User"
                  onChange={(e) => setUserIdFilter(e.target.value)}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {uniqueUserIds.map((uid) => (
                    <MenuItem key={uid} value={uid}>
                      {getUserName(uid)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>High School</InputLabel>
                <Select
                  value={highSchoolFilter}
                  label="High School"
                  onChange={(e) => setHighSchoolFilter(e.target.value)}
                >
                  <MenuItem value="">All Schools</MenuItem>
                  {uniqueHighSchools.map((school) => (
                    <MenuItem key={school} value={school}>
                      {school}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Event</InputLabel>
                <Select
                  multiple
                  value={eventFilter}
                  label="Event"
                  onChange={(e) => setEventFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {uniqueEvents.map((event) => (
                    <MenuItem key={event} value={event}>
                      {event}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Event Detail"
                value={eventDetailFilter}
                onChange={(e) => setEventDetailFilter(e.target.value)}
                placeholder="Filter by event detail..."
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Results Summary */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredLogs.length} of {activityLogs.length} total records
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={fetchActivityLogs}
              disabled={loading}
            >
              Refresh Data
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {(loading || userDataLoading) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Activity Log Table */}
        {!loading && !userDataLoading && !error && (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>High School</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Event Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No activity logs found for the selected criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getUserName(log.uid)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getUserHighSchool(log.uid)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {log.actionTaken}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {formatEventDetail(log.actionTaken, log.actionSubject)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default Reports;
